import { Link } from "react-router-dom";

import { formatDate } from "components/helpers";
import {
  PanelField,
  PanelSection,
} from "components/sidebar/sections/InfoPanel";
import TagsPanelSection from "components/sidebar/sections/TagsPanelSection";
import { noTag } from "constants/Tag";

import { EM_DASH, formatCurrency, formatPercentage } from "./grid/cellHelpers";
import { resolveRelationshipTarget } from "./relationshipRoutes";

// Mirrors RelationshipCell's stale-link contract: live target → link (plain
// text without a detail route), soft-deleted → "(deleted)" marker,
// unresolved → "#<id> (unavailable)".
const relationshipValue = ({ value, display, column }) => {
  if (!display || display.label == null) {
    return (
      <span className="tw:text-muted-foreground">#{value} (unavailable)</span>
    );
  }
  if (display.deleted) {
    return (
      <span>
        {display.label}{" "}
        <span className="tw:text-muted-foreground">(deleted)</span>
      </span>
    );
  }
  const target = resolveRelationshipTarget(
    column?.options?.target_type,
    column?.options
  );
  if (!target?.detailRoute) return display.label;
  return <Link to={target.detailRoute(value)}>{display.label}</Link>;
};

const formatFieldValue = field => {
  const { type, value } = field;
  if (value == null || value === "") return null;
  if (type === "boolean") return value ? "Yes" : "No";
  if (type === "percentage") return formatPercentage(value);
  if (type === "currency") return formatCurrency(value);
  if (type === "multi_select" || type === "list") {
    if (!Array.isArray(value) || value.length === 0) return null;
    return value;
  }
  if (type === "relationship") return relationshipValue(field);
  return String(value);
};

const formatTimestamp = value =>
  value == null || value === "" ? null : formatDate(value);

const buildInfoFields = (row, detailRows, showEmptyFields) => {
  const fields = [
    { label: "ID", value: row.id },
    { label: "Created at", value: formatTimestamp(row.created_at) ?? EM_DASH },
    { label: "Updated at", value: formatTimestamp(row.updated_at) ?? EM_DASH },
    { label: "Created by", value: row.created_by?.full_name ?? EM_DASH },
    { label: "Updated by", value: row.updated_by?.full_name ?? EM_DASH },
  ];
  for (const field of detailRows) {
    const formatted = formatFieldValue(field);
    fields.push({
      label: field.label,
      value: formatted == null && showEmptyFields ? EM_DASH : formatted,
    });
  }
  return fields;
};

// Read-only information for a custom-table row detail rail. The row fetch does
// not accept a show_disabled_tags parameter, so disabled tags are filtered
// client-side from the applied-tag list while the dropdown reveals them via its
// own prop.
const CustomTableRowInfoPanel = ({
  row,
  detailRows,
  showEmptyFields,
  supportsTags,
  modelType,
  rowId,
  showDisabledTags,
  onUpdate,
}) => {
  const fields = buildInfoFields(row, detailRows, showEmptyFields);

  // ``[noTag]`` keeps the section visible with a "No Tags" placeholder when the
  // row has none, matching the other detail pages.
  const rowTags = row.tags ?? [];
  const displayTags = rowTags.length ? rowTags : [noTag];
  const modelTags = showDisabledTags
    ? displayTags
    : displayTags.filter(({ enabled }) => enabled);

  return (
    <>
      <PanelSection>
        {fields.map(({ label, value }) => (
          <PanelField key={label} label={label}>
            {value}
          </PanelField>
        ))}
      </PanelSection>

      {supportsTags && (
        <TagsPanelSection
          title="Tags"
          modelType={modelType}
          record={{ id: Number(rowId), tags: rowTags }}
          appliedTags={modelTags}
          showDisabledTags={showDisabledTags}
          onUpdate={onUpdate}
        />
      )}
    </>
  );
};

export default CustomTableRowInfoPanel;
